/**
 * Composant de gestion du mot de passe
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Gestion du changement de mot de passe avec recommandations de sécurité.
 */

  // Client Component pour Next.js

import { useState } from 'react';
import { ChangePasswordForm } from '../forms/ChangePasswordForm';
import { useSecurityAlert } from '../ui/SecurityAlert';
import { SecurityAlert } from '../ui/SecurityAlert';

/**
 * Props du composant PasswordSettings
 */
export interface PasswordSettingsProps {
  /** ID de l'utilisateur */
  userId: string;
  /** Date du dernier changement de mot de passe (ISO string) */
  lastPasswordChange?: string;
  /** Callback après changement réussi */
  onSuccess?: () => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant PasswordSettings
 *
 * @example
 * ```tsx
 * function PasswordPage() {
 *   const { user } = useAuth();
 *
 *   return (
 *     <PasswordSettings
 *       userId={user.id}
 *       lastPasswordChange={user.dernierChangementMotDePasse}
 *       onSuccess={() => toast.success('Mot de passe modifié')}
 *     />
 *   );
 * }
 * ```
 */
export function PasswordSettings({
  userId,
  lastPasswordChange,
  onSuccess,
  onError,
  className = '',
}: PasswordSettingsProps) {
  const { alert, showAlert, hideAlert } = useSecurityAlert();
  const [showForm, setShowForm] = useState(false);

  /**
   * Gérer le succès du changement
   */
  const handleSuccess = () => {
    setShowForm(false);
    showAlert('success','Mot de passe modifié avec succès', 'success');
    onSuccess?.();
  };

  /**
   * Gérer l'erreur du changement
   */
  const handleError = (error: string) => {
    showAlert('error',error);
    onError?.(error);
  };

  /**
   * Formater la date
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Jamais modifié';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Aujourd\'hui';
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
    if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;

    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(date);
  };

  /**
   * Vérifier si le mot de passe est ancien (> 90 jours)
   */
  const isPasswordOld = (): boolean => {
    if (!lastPasswordChange) return true;

    const date = new Date(lastPasswordChange);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    return diffDays > 90;
  };

  return (
    <div className={`w-full max-w-4xl ${className}`}>
      {/* Titre */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-content">
          Mot de passe
        </h2>
        <p className="mt-1 text-sm text-content-muted">
          Gérez le mot de passe de votre compte
        </p>
      </div>

      {/* Alerte */}
      {alert && (
        <div className="mb-6">
          <SecurityAlert
           {...alert}
          />
        </div>
      )}

      <div className="space-y-6">
        {/* Informations sur le mot de passe */}
        <div className="bg-surface rounded-lg border border-border p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-accent-light rounded-lg flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-content">
                  Mot de passe actuel
                </h3>
                <p className="mt-1 text-sm text-content-muted">
                  Dernière modification : {formatDate(lastPasswordChange)}
                </p>
                {isPasswordOld() && (
                  <div className="mt-2 flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-warning"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-warning">
                      Nous recommandons de changer votre mot de passe régulièrement
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recommandations de sécurité */}
        <div className="bg-accent-light border border-accent rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-accent mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-accent">
                Recommandations pour un mot de passe fort
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-accent">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Utilisez au moins 12 caractères</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Combinez lettres majuscules et minuscules</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Incluez des chiffres et des caractères spéciaux</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Évitez les mots courants et les informations personnelles</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>N'utilisez pas le même mot de passe pour plusieurs comptes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bouton ou Formulaire de changement */}
        {!showForm ? (
          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-content mb-2">
                Changer votre mot de passe
              </h3>
              <p className="text-sm text-content-muted mb-4">
                Assurez-vous de choisir un mot de passe fort et unique
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="px-6 py-3 bg-primary hover:bg-primary-hover text-content-inverted font-medium rounded-lg transition-colors"
              >
                Modifier le mot de passe
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-surface rounded-lg border border-border p-6">
            <ChangePasswordForm
              userId={userId}
              onSuccess={handleSuccess}
              onError={handleError}
              showCancel={true}
              onCancel={() => setShowForm(false)}
            />
          </div>
        )}

        {/* Conseils supplémentaires */}
        <div className="bg-surface-raised border border-border rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-content-subtle mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-content-muted">
                Protégez votre compte
              </h4>
              <p className="mt-1 text-sm text-content-muted">
                En plus d'un mot de passe fort, activez l'authentification à deux facteurs pour une
                protection maximale de votre compte. Cela empêchera l'accès non autorisé même si
                quelqu'un découvre votre mot de passe.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
